import { abrirMaterial, baseDe } from '../look/materiais';

/** Armas que o simulador resolve no `d`. O seletor só mostra estas. */
export const ARMAS_COMBATE = [
  'katana',
  'dagger',
  'large-battle-axe',
  'gnarled-staff',
  'bow',
  'heavy-crossbow'
] as const;

/** A base ou a variante de material (`bow@oak`, `katana@valorite`). */
export type ArmaCombateId = (typeof ARMAS_COMBATE)[number] | `${(typeof ARMAS_COMBATE)[number]}@${string}`;

const IDS = new Set<string>(ARMAS_COMBATE);

/** A variante só vale com material da família da base (`bow@valorite` não existe). */
export function ehArmaCombate(id: string | null | undefined): id is ArmaCombateId {
  if (!id) return false;
  if (IDS.has(id)) return true;
  const { base, material } = abrirMaterial(id);
  return base !== id && IDS.has(base) && !!material;
}

export type FichaAnimArma = {
  foot: string;
  horseBody: string;
  overlay: string;
  overlayHit: string;
  idleFoot?: string;
  walkFoot?: string;
  idleHorse?: string;
  walkHorse?: string;
};

const ARCO = new Set<string>(['bow', 'heavy-crossbow']);
const MACAS = new Set<string>(['gnarled-staff']);
const ESGRIMA = new Set<string>(['dagger']);
const MACHADOS = new Set<string>(['large-battle-axe']);

/** Item → pasta em `character/weapons/`. Sem pasta própria, usa a silhueta irmã. */
const OVERLAY: Readonly<Record<string, string>> = {
  katana: 'katana',
  dagger: 'dagger',
  'large-battle-axe': 'large-battle-axe',
  'gnarled-staff': 'gnarled-staff',
  bow: 'bow',
  'heavy-crossbow': 'heavy-crossbow',
  // A machadinha da coleta (resources/tools/Hatchet.vd). Vai no slot de arma, mas não é arma de combate.
  hatchet: 'hatchet',
  'golden-hatchet': 'hatchet',
  // A picareta da mineração (resources/tools/pickaxe.vd), igual à machadinha.
  pickaxe: 'pickaxe',
  'golden-pickaxe': 'pickaxe',
  // A skin de cajado (techspec-skins §03): só aparência, no lugar de uma maça.
  'natal-bengala': 'natal-bengala',
  'cajado-fundador': 'cajado-fundador'
};

/** Pose do corpo por silhueta de arma. O que não está aqui bate como desarmado. */
const POSE: Readonly<Record<string, { pe: string; cavalo: string }>> = {
  katana: { pe: 'attack-slash-1h', cavalo: 'attack-slash' },
  dagger: { pe: 'attack-pierce-1h', cavalo: 'attack-slash' },
  'large-battle-axe': { pe: 'attack-slash-2h', cavalo: 'attack-slash' },
  'gnarled-staff': { pe: 'attack-bash-2h', cavalo: 'attack-slash' },
  bow: { pe: 'attack-bow', cavalo: 'attack-bow' },
  'heavy-crossbow': { pe: 'attack-crossbow', cavalo: 'attack-crossbow' },
  hatchet: { pe: 'attack-slash-2h', cavalo: 'attack-slash' },
  pickaxe: { pe: 'attack-slash-2h', cavalo: 'attack-slash' },
  'natal-bengala': { pe: 'attack-bash-2h', cavalo: 'attack-slash' },
  'cajado-fundador': { pe: 'attack-bash-2h', cavalo: 'attack-slash' }
};
const PADRAO = { pe: 'attack-slash-2h', cavalo: 'attack-slash' };

/** Folha do corpo, overlay e poses. Sem idle/walk a pé, o motor usa a folha a cavalo. */
export function animDaArma(id: string): FichaAnimArma | null {
  const overlay = OVERLAY[baseDe(id)];
  if (!overlay) return null;
  const pose = POSE[overlay] ?? PADRAO;
  return {
    foot: pose.pe,
    horseBody: pose.cavalo,
    overlay,
    overlayHit: 'attack',
    idleFoot: 'idle',
    walkFoot: 'walking',
    idleHorse: 'idle',
    walkHorse: 'walking'
  };
}

export function ehArco(id: string): boolean {
  return ARCO.has(baseDe(id));
}

export function ehMaca(id: string): boolean {
  return MACAS.has(baseDe(id));
}

export function ehEsgrima(id: string): boolean {
  return ESGRIMA.has(baseDe(id));
}

export function ehMachado(id: string): boolean {
  return MACHADOS.has(baseDe(id));
}

export function ehEspada(id: string): boolean {
  return ehArmaCombate(id) && !ehArco(id) && !ehMaca(id) && !ehEsgrima(id) && !ehMachado(id);
}

export type DummyTrainSkill = 'swordsmanship' | 'maceFighting' | 'fencing' | 'axing' | 'magery';

export function armaServeSkill(defId: string, skill: DummyTrainSkill): boolean {
  if (skill === 'swordsmanship') return ehEspada(defId);
  if (skill === 'maceFighting') return ehMaca(defId);
  if (skill === 'fencing') return ehEsgrima(defId);
  if (skill === 'axing') return ehMachado(defId);
  return false;
}

/** As skins de cajado: a camada do traje no fio (`t:<id>:0`) → a arma que o motor pinta. */
const SKINS_DE_CAJADO: ReadonlySet<string> = new Set(['natal-bengala', 'cajado-fundador']);

/** A camada do fio é uma skin de cajado (`t:natal-bengala:0`)? O motor guarda ela no fio, mas não pinta no corpo. */
export function ehCamadaDeCajado(parte: string): boolean {
  const p = parte.trim();
  return p.startsWith('t:') && SKINS_DE_CAJADO.has(p.split(':')[1] ?? '');
}

/**
 * A arma que aparece (techspec-skins §03): com uma skin de cajado no traje, qualquer maça na mão vira a skin. Só a
 * aparência: o som, a skill e o resto do jogo continuam com a arma de verdade.
 */
export function armaComSkin(weapon: string, cloth: string | null | undefined): string {
  if (!weapon || !ehMaca(weapon) || !cloth) return weapon;
  for (const parte of cloth.split('+')) {
    if (!parte.startsWith('t:')) continue;
    const id = parte.split(':')[1] ?? '';
    if (SKINS_DE_CAJADO.has(id)) return id;
  }
  return weapon;
}
